import React from 'react';
import Chart from './Chart'
import axios from 'axios'
import {
    FormControl,
    InputLabel,
    OutlinedInput,
    MenuItem,
    Select,
    TextField,
    Button,
    makeStyles,
    createStyles,
    Tooltip,
    Chip
} from "@material-ui/core";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile"
import {Theme} from "@material-ui/core/styles"
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import MaterialTable from "./MaterialTable";
import copy from 'copy-to-clipboard';

const useStyles = makeStyles((theme: Theme) => createStyles({
    lineChart: {
        marginBottom: '30px'
    },
    paperStyles: {
        display: 'flex',
        flexDirection: 'column',
        padding: '10px 20px 20px 20px',
        background: theme.palette.primary.main,
        borderRadius: '0 21px 0 21px',
        backgroundImage: `url("https://www.coderelite.com/static/media/BLANK_MAP_2021.svg")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
    }
}))

interface LookupParams {
    host: string;
    protocol: string;
    ipVersion: string;
    location: number;
}

interface SonarAgentObj {
    id: number;
    name: string;
    label: string;
    location: string;
    country: string;
    region: string;
}

export interface TraceResultObj {
    hop: string;
    host: string;
    ip: string;
    rtnn: string;
}

export interface AgentList {
    location: string;
    name: string;
    id: number;
}

// This method grabs the sonar agent locations from the sonar API
function getSonarAgents() {
    return axios.get('https://dnstb-backend.herokuapp.com/agents')
}

function App() {
    const classes = useStyles()
    const inputLabel = React.useRef(null);
    const [labelWidth, setLabelWidth] = React.useState(0);
    const [agents, setAgents] = React.useState<Array<AgentList>>([]);
    // const [ipVersion, setIpVersion] = React.useState('IPV4')
    // const [validHost, setValidHost] = React.useState(true)
    // const [host, setHost] = React.useState('')
    const domainRegex = /([a-z0-9|-]+\.)*[a-z0-9|-]+\.[a-z]+/
    const ipRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/
    const ipvSixRegex = /(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))/
    const [queryParams, setQueryParams] = React.useState<LookupParams>(
        {
            protocol: "UDP",
            location: 1,
            host: "",
            ipVersion: "IPV4",
        }
    )
    const defaultData = Array.from(Array(15)).map((x, index) => {
        return {hop: (index + 1).toString(), host: "n/a", ip: "n/a", rtnn: "n/a"}
    })
    const [traceResults, setTraceResults] = React.useState<Array<TraceResultObj> | []>(defaultData)
    const [traceRunning, setTraceRunning] = React.useState(false)
    const [formError, setFormError] = React.useState(false)
    const [rawResults, setRawResults] = React.useState("")


    React.useEffect(() => {
        if (formError) {
            setFormError(false)
        }
        // @ts-ignore
        setLabelWidth(inputLabel.current.offsetWidth);

        /**
         * This function makes a call to the backend to grab all Sonar Agents.
         * Then it enriches the data so that it can be use in future DNS request and saves that data to an Arry in state.
         */
        getSonarAgents().then(response => {
            let agentArr: Array<AgentList> = [];

            response.data.map((agentObj: SonarAgentObj, index: number) => {

                // converts agentArr to a simple array with location names
                const tempAgenArr = () => agentArr.map((agentList: AgentList) => {
                    return agentList.location
                })

                if (!tempAgenArr().find((location: string) => location === agentObj.location)) {
                    agentArr = [...agentArr, {location: agentObj.location, name: agentObj.name, id: agentObj.id}]
                }
            })
            setAgents(agentArr)
        })
    }, [])

    /**
     * Makes an API call which returns the results of a DNS Lookup based on the provided params
     * @param queryParams
     * @return Promise for an object containing the results of the DNS Lookup
     */
    function traceLookup(): Promise<any> {
        return axios({
            method: 'post',
            url: 'https://dnstb-backend.herokuapp.com/trace',
            //this is for testing backend locally
            // url: 'http://localhost:80/dig',
            data: queryParams
        }).then(response => response.data)
    }

    function handleHostChange(host: string) {
        setQueryParams({...queryParams, host: host})
    }

    function handleIpVersionChange(ipVersion: string) {
        setQueryParams({...queryParams, ipVersion: ipVersion})
    }

    function handleLocationChange(location: number) {
        setQueryParams({...queryParams, location: location})
    }

    function handleProtocolChange(protocol: string) {
        setQueryParams({...queryParams, protocol: protocol})
    }

    function copyResults() {
        copy(rawResults)
    }

    function handleSubmit() {
        setTraceRunning(true)
        if (queryParams.host.match(domainRegex) !== null || queryParams.host.match(ipRegex) !== null || queryParams.host.match(ipvSixRegex) !== null) {

            traceLookup().then((response) => {

                if (response.length === 0) {
                    setTraceResults(defaultData)
                    setTraceRunning(false)
                    return
                }

                const responseObj = response[0].result.split('\n').slice(1, 16).map((hop: any) => {
                    const hopArr = hop.split('  ')
                    hopArr[0] = hopArr[0].replace(' ', '')

                    if (hopArr[2] === undefined) {
                        const hopObj = {hop: hopArr[0], host: '*', ip: 'n/a', rtnn: 'n/a'}
                        return hopObj
                    }

                    const hopHost = hopArr[1].split(' ')[0]
                    const hopIp = hopArr[1].split(' ')[1].replace('(', '').replace(')', '')
                    const hopObj = {hop: hopArr[0], host: hopHost, ip: hopIp, rtnn: hopArr[2]}
                    return hopObj
                })
                setFormError(false)
                setRawResults(response[0].result)
                setTraceResults(responseObj)
                setTraceRunning(false)
            })
        } else {
            setFormError(true)
            setTraceRunning(false)
            return
        }

    }

    return (
        <div className="App">
            <div style={{display: 'flex', width: '90vw', margin: '10px 0 10px 0'}}>
                <TextField
                    style={{marginRight: '10px'}}
                    value={queryParams.host}
                    onChange={(event) => {
                        handleHostChange(event.target.value)
                    }}
                    id="outlined-basic"
                    label="Host"
                    variant="outlined"
                    InputLabelProps={{
                        shrink: true,
                    }}
                    error={formError}

                />
                <FormControl variant="outlined">
                    <InputLabel
                        ref={inputLabel}
                        id="demo-simple-select-outlined-label"
                        shrink
                    >IP Version</InputLabel>
                    <Select
                        style={{marginRight: '10px', width: '110px'}}
                        labelId="demo-simple-select-outlined-label"
                        id="demo-simple-select-outlined"
                        value={queryParams.ipVersion}
                        onChange={(event: any) => {
                            handleIpVersionChange(event.target.value)
                        }}
                        label="Record Type"
                        input={
                            <OutlinedInput
                                notched
                                labelWidth={95}
                                name="Record Type"
                                id="outlined-age-always-notched"
                            />
                        }
                    >
                        <MenuItem value='IPV4'>IPv4</MenuItem>
                        <MenuItem value='IPV6'>IPv6</MenuItem>

                    </Select>
                </FormControl>
                <FormControl variant="outlined">
                    <InputLabel
                        ref={inputLabel}
                        id="demo-simple-select-outlined-label"
                        shrink
                    >Location</InputLabel>
                    <Select
                        style={{marginRight: '10px', width: '227px'}}
                        labelId="demo-simple-select-outlined-label"
                        id="demo-simple-select-outlined"
                        value={queryParams.location}
                        onChange={(event: any) => handleLocationChange(event.target.value)}
                        label="Age"
                        input={
                            <OutlinedInput
                                notched
                                labelWidth={labelWidth}
                                name="Location"
                                id="outlined-age-always-notched"
                            />
                        }
                    >
                        {agents.map((agent: any, index) => {
                            return <MenuItem key={index} value={agent.id}>{agent.location}</MenuItem>
                        })}
                    </Select>
                </FormControl>
                <FormControl variant="outlined">
                    <InputLabel
                        ref={inputLabel}
                        id="demo-simple-select-outlined-label"
                        shrink
                    >Protocol</InputLabel>
                    <Select
                        style={{marginRight: '0', width: '100px'}}
                        labelId="demo-simple-select-outlined-label"
                        id="demo-simple-select-outlined"
                        value={queryParams.protocol}
                        onChange={(event: any) => handleProtocolChange(event.target.value)}
                        label="Age"
                        input={
                            <OutlinedInput
                                notched
                                labelWidth={labelWidth}
                                name="Location"
                                id="outlined-age-always-notched"
                            />
                        }
                    >
                        <MenuItem value="UDP">UDP</MenuItem>
                        {/*<MenuItem value="TCP">TCP</MenuItem>*/}
                        <MenuItem value="ICMP">ICMP</MenuItem>
                    </Select>
                </FormControl>
                <Button
                    style={{marginLeft: '10px'}}
                    size='small'
                    variant="contained"
                    color="primary"
                    endIcon={<PlayArrowIcon/>}
                    onClick={handleSubmit}
                >
                    Run
                </Button>
            </div>
            <div className={classes.paperStyles}>
                <div style={{alignSelf: 'flex-end', marginBottom: '10px'}}>
                    <Tooltip title={'Copy results from raw output'}>
                        <Chip disabled={rawResults.length > 0 ? false : true} color={"secondary"} icon={<InsertDriveFileIcon color={"primary"}/>} label="Copy Results" onClick={copyResults} />
                    </Tooltip>
                </div>
                <div className={classes.lineChart}>
                    <Chart result={traceResults} traceRunning={traceRunning}/>
                </div>
                <div>
                    <MaterialTable result={traceResults} traceRunning={traceRunning}/>
                </div>
            </div>
        </div>
    )
}

export default App;
